import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "./scrollUtils";

const LINKS = [
  { label: "Chi Sono", href: "#mi-presento" },
  { label: "Percorsi", href: "#percorsi" },
  { label: "Servizi", href: "#servizi" },
  { label: "Testimonianze", href: "#testimonianze" },
  { label: "FAQ", href: "#faq" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [onDark, setOnDark] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const banner = document.getElementById("masterclass-banner");
      if (banner) {
        const rect = banner.getBoundingClientRect();
        setOnDark(rect.bottom > 72);
      } else {
        setOnDark(false);
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    // Il banner Masterclass appare in modo asincrono: ricalcola i colori quando cambia stato.
    window.addEventListener("mc-banner-state", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mc-banner-state", onScroll);
    };
  }, []);

  const go = (href) => {
    setOpen(false);
    // Defer so the mobile menu collapse doesn't fight the scroll animation.
    setTimeout(() => scrollToId(href), 60);
  };

  const light = onDark && !scrolled;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-crema/80 backdrop-blur-xl border-b border-espresso/10 py-3"
          : "bg-transparent py-5"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-between">
        <button
          onClick={() => go("#hero")}
          data-testid="header-logo"
          className="text-left leading-none group"
        >
          <span className={`block font-display text-lg sm:text-xl tracking-tight transition-colors duration-300 ${light ? "text-crema" : "text-espresso"}`}>
            Alina Cossali
          </span>
          <span className={`block font-mono text-[10px] uppercase tracking-[0.3em] group-hover:text-fuchsiabrand transition-colors duration-300 ${light ? "text-crema/60" : "text-stone"}`}>
            Consulente d'Immagine
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.href.slice(1)}`}
              className={`font-mono text-xs uppercase tracking-[0.15em] hover:text-fuchsiabrand transition-colors duration-300 ${light ? "text-crema/80" : "text-stone"}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#hero")}
            data-testid="nav-cta"
            className={`px-5 py-2.5 rounded-full font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${light ? "bg-crema text-espresso hover:bg-fuchsiabrand hover:text-white" : "bg-espresso text-crema hover:bg-fuchsiabrand"}`}
          >
            Contattami
          </button>
        </nav>

        <button
          className={`md:hidden transition-colors duration-300 ${light ? "text-crema" : "text-espresso"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-crema/95 backdrop-blur-xl border-t border-espresso/10 mt-3"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  data-testid={`mobile-nav-${l.href.slice(1)}`}
                  className="text-left font-serif text-2xl text-espresso"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#hero")}
                className="bg-fuchsiabrand text-white px-5 py-3 rounded-full font-mono text-xs uppercase tracking-[0.15em] text-center"
              >
                Contattami
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
